/**
 * On-demand loader for the Google Maps JS API (Places library), used by the
 * recipient address autocomplete. Injects the script once and resolves with the
 * `places` namespace when ready — or `null` if it can't load (no key, network, or a
 * `RefererNotAllowedMapError` on a host not allow-listed for the key). Callers fall
 * back to a plain input on `null`, so autocomplete is always a progressive enhancement.
 *
 * Minimal local typings (only what we use) so we don't pull in `@types/google.maps`.
 */

import type { AddressParts } from "@/interfaces/redemption";

export interface GooglePlace {
  address_components?: Array<{ types: string[]; long_name: string; short_name: string }>;
}
export interface GooglePlacesAutocomplete {
  addListener(event: "place_changed", cb: () => void): void;
  getPlace(): GooglePlace;
}
export interface GooglePlacesNamespace {
  Autocomplete: new (
    input: HTMLInputElement,
    opts?: {
      componentRestrictions?: { country: string | string[] };
      types?: string[];
      fields?: string[];
    },
  ) => GooglePlacesAutocomplete;
}

declare global {
  interface Window {
    google?: {
      maps?: {
        places?: GooglePlacesNamespace;
        /** Bootstrap loader (present with `loading=async`) — resolves once a library is ready. */
        importLibrary?: (name: string) => Promise<unknown>;
      };
    };
  }
}

const SCRIPT_ID = "gmaps-places-sdk";
let loader: Promise<GooglePlacesNamespace | null> | null = null;

export function loadGooglePlaces(
  apiKey: string | null | undefined,
): Promise<GooglePlacesNamespace | null> {
  if (typeof window === "undefined" || !apiKey) return Promise.resolve(null);
  if (window.google?.maps?.places) return Promise.resolve(window.google.maps.places);
  if (loader) return loader;

  loader = new Promise((resolve) => {
    // With `loading=async`, the script's `load` event fires when the bootstrap is
    // ready — NOT when the Places library is attached. Resolving on `load` alone
    // hands back `undefined`, so the field degrades to a plain input on first mount
    // and only "works" on a later remount (once the library finished in the
    // background) — the race behind the "autocomplete only appears after an edit
    // round-trip" bug. So: kick the async library load, then poll until
    // `google.maps.places` actually exists before resolving (v1 waited for the full
    // window `load` for the same reason). Resolves null on timeout → plain input.
    const READY_TIMEOUT_MS = 10000;
    const settleWhenReady = () => {
      window.google?.maps?.importLibrary?.("places").catch(() => undefined);
      const started = Date.now();
      const tick = () => {
        const places = window.google?.maps?.places;
        if (places) {
          resolve(places);
        } else if (Date.now() - started > READY_TIMEOUT_MS) {
          resolve(null); // never became ready → graceful fallback
        } else {
          window.setTimeout(tick, 60);
        }
      };
      tick();
    };
    const fail = () => {
      loader = null; // allow a later retry
      resolve(null);
    };

    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener("error", fail);
      settleWhenReady(); // may already be loading — poll until Places attaches
      return;
    }
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.async = true;
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      apiKey,
    )}&libraries=places&loading=async`;
    s.addEventListener("load", settleWhenReady);
    s.addEventListener("error", fail);
    document.head.appendChild(s);
  });
  return loader;
}

/**
 * Map a Google `place` (from an Autocomplete `place_changed`) → our
 * `{ street, city, state, zip }`. Mirrors v1's parse: street_number + route → street,
 * `administrative_area_level_1` short_name → state, `postal_code` → zip. Returns null
 * when there's no usable street (so callers can ignore an incomplete selection).
 */
export function parsePlace(place: GooglePlace): AddressParts | null {
  const comps = place.address_components;
  if (!comps) return null;
  let streetNumber = "";
  let route = "";
  let city = "";
  let state = "";
  let zip = "";
  for (const comp of comps) {
    const t = comp.types;
    if (t.includes("street_number")) streetNumber = comp.long_name;
    if (t.includes("route")) route = comp.long_name;
    if (t.includes("locality")) city = comp.long_name;
    else if (!city && t.includes("sublocality")) city = comp.long_name;
    if (t.includes("administrative_area_level_1")) state = comp.short_name;
    if (t.includes("postal_code")) zip = comp.long_name;
  }
  const street = [streetNumber, route].filter(Boolean).join(" ");
  return street ? { street, city, state, zip } : null;
}
